import {
  Children,
  Fragment,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';

export type ColumnVisibilityMap = Record<string, boolean>;

const STORAGE_KEY_PREFIX = 'col_vis_';

export function loadStoredColumnVisibility(resource: string): ColumnVisibilityMap {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + resource);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function saveColumnVisibility(
  resource: string,
  visibility: ColumnVisibilityMap,
): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + resource, JSON.stringify(visibility));
  } catch {
    // ignore storage errors
  }
}

export interface ColumnDefinition {
  key: string;
  label: React.ReactNode;
  hiddenByDefault: boolean;
  field: ReactElement;
}

function flattenFields(fields?: ReactNode): ReactNode[] {
  const children = Children.toArray(fields);
  return children.flatMap((child) => {
    if (isValidElement(child) && child.type === Fragment) {
      return flattenFields((child.props as any).children);
    }
    return [child];
  });
}

export function getColumnKey(field: ReactNode, index: number): string | null {
  if (!isValidElement(field)) return null;
  const props = field.props as any;
  const label = props.label;
  return (
    props.columnKey ||
    props.source ||
    props.sortBy ||
    (typeof label === 'string' || typeof label === 'number'
      ? `${label}`
      : null) ||
    `${index}`
  );
}

export function getColumnDefinitions(fields?: ReactNode): ColumnDefinition[] {
  return flattenFields(fields).reduce<ColumnDefinition[]>(
    (acc, field, index) => {
      if (!isValidElement(field)) {
        return acc;
      }
      const key = getColumnKey(field, index);
      if (!key) {
        return acc;
      }
      const props = field.props as any;
      const label = props.label === '' ? key : (props.label ?? key);
      acc.push({
        key,
        label,
        hiddenByDefault: !!props.hiddenByDefault,
        field: field as React.ReactElement,
      });
      return acc;
    },
    [],
  );
}

export function getColumnVisibilityMap(
  columns: ColumnDefinition[],
  storedVisibility: ColumnVisibilityMap = {},
): ColumnVisibilityMap {
  return columns.reduce<ColumnVisibilityMap>((acc, column) => {
    const hasStoredValue = Object.prototype.hasOwnProperty.call(
      storedVisibility,
      column.key,
    );
    acc[column.key] = hasStoredValue
      ? storedVisibility[column.key]
      : !column.hiddenByDefault;
    return acc;
  }, {});
}

export function getVisibleColumns(
  columns: ColumnDefinition[],
  visibility: ColumnVisibilityMap,
): ReactElement[] {
  const visibleColumns = columns.filter(
    (column) => visibility[column.key] !== false,
  );

  if (!visibleColumns.length && columns.length) {
    return columns.map((column) => column.field);
  }

  return visibleColumns.map((column) => column.field);
}
